import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";
// NOTE: Do not use redux hooks inside react-pdf render path. Rely on inherited font size

export const TemplateFivePDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) =>{
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return (
  <div
  onClick={handleClickCallback}
    style={{
      ...styles.flexCol,
      // ...style,
      marginTop: `-${"0.75pt"}`,
      cursor:"pointer"
    }}
  >
    <View style={{ ...styles.flexRow }}>
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            width: "20%",
            backgroundColor: themeColor,
            padding: `${spacing[3]} ${spacing[3]} ${spacing[3]} ${spacing[10]}`,
            borderTop: `1pt solid ${themeColor}`,
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: "black",
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      <View
        style={{
          width: "80%",
          ...styles.flexCol,
          gap: spacing[1.5],
          padding: `${spacing[3]} ${spacing[10]} ${spacing[3]} ${spacing[5]}`,
        }}
      >
        {children}
      </View>
    </View>
    <View
      style={{
        width: "92%",
        height: "1.25pt",
        position: "absolute",
        backgroundColor: "black",
        left: "5%",
      }}
    />
  </div>
)}

export const TemplateFiveProfileSection = ({
  heading,
  style = {},
  children,
}: {
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => (
  <View
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      marginBottom: spacing["5"],
      ...style,
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
            color: DEFAULT_FONT_COLOR,
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
      </View>
    )}
    {children}
  </View>
);

// Enhanced version with optimized spacing and orphan heading prevention
export const TemplateFivePDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  // Group content to prevent orphan headings
  const groupedChildren = React.Children.toArray(children);
  const firstChild = groupedChildren[0];
  const restChildren = groupedChildren.slice(1);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        marginTop: "-0.75pt", // Reduced from original
        cursor: "pointer"
      }}
    >
      {/* Wrap heading with first child only to prevent orphan headings */}
      {heading && firstChild && (
        <View style={{ ...styles.flexRow }} wrap={false}> {/* Only first row prevented from splitting */}
          <View
            style={{
              ...styles.flexCol,
              gap: "6pt", //  spacing[1] (12pt) to 6pt
              width: "20%",
              backgroundColor: themeColor,
              padding: `9pt 9pt 9pt ${spacing[10]}`, // spacing[3] (36pt) to 9pt
              borderTop: `1pt solid ${themeColor}`,
            }}
          >
              {/* Use dynamic font size from settings when available so user changes reflect here */}
              <Text
                style={{
                  fontWeight: "bold",
                  letterSpacing: "0.3pt",
                  color: "black",
                  /* Let this inherit the page's base font size so user-selected font size applies. */
                  lineHeight: "1.3",
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              >
                {heading}
              </Text>
          </View>
          <View
            style={{
              width: "80%",
              ...styles.flexCol,
              gap: "3pt", // spacing[1.5] (18pt) to 3pt
              padding: `9pt ${spacing[10]} 9pt 15pt`, 
            }}
          >
            {firstChild}
          </View>
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View style={{ ...styles.flexRow }}>
          <View
            style={{
              ...styles.flexCol,
              gap: "6pt",
              width: "20%",
              backgroundColor: themeColor,
              padding: `9pt 9pt 9pt ${spacing[10]}`,
              borderTop: `1pt solid ${themeColor}`,
            }}
          >
            {/* Use dynamic font size from settings when available so user changes reflect here */}
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "black",
                /* Let this inherit the page's base font size so user-selected font size applies. */
                lineHeight: "1.3",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
          <View
            style={{
              width: "80%",
              ...styles.flexCol,
              gap: "3pt",
              padding: `9pt ${spacing[10]} 9pt 15pt`, 
            }}
          />
        </View>
      )}
      
      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && restChildren.map((child, index) => (
        <View key={`rest-${index}`} style={{ ...styles.flexRow }}>
          <View 
            style={{ 
              width: "20%",
              backgroundColor: themeColor, // Keep the colored background
              padding: `9pt 9pt 9pt ${spacing[10]}`,
            }} 
          /> {/* Colored space to maintain visual consistency */}
          <View
            style={{
              width: "80%",
              ...styles.flexCol,
              gap: "3pt",
              padding: `9pt ${spacing[10]} 9pt 15pt`, 
            }}
          >
            {child}
          </View>
        </View>
      ))}
      <View
        style={{
          width: "92%",
          height: "1.25pt",
          position: "absolute",
          backgroundColor: "black",
          left: "5%",
        }}
      />
    </div>
  );
};
